import React, { useCallback, useState } from 'react';
import { Button, Tabs } from 'antd';
import dayjs from 'dayjs';
import { useGetAwardOptionsQuery } from '../calendar/eventAdminCalendarSlice';
import './assets/css/stats.css';
import StatisticsTabs from './components/StatisticsTabs';
import { downloadCsv, statsJsonToCsv } from './utils/statsHelpers';
import {
  useGetComplianceAdminStatisticsQuery,
  useGetEventTypesForRDOQuery,
} from '../../../rdo_compliance_management/complianceManagerSlice';
import StatsDateRangeSelect from '../dashboard/components/StatsDateRangeSelect';

const DEFAULT_START = dayjs().subtract(7, 'day');
const DEFAULT_END = dayjs();

function ComplianceAdminStatistics() {
  const [start, setStart] = useState(DEFAULT_START);
  const [end, setEnd] = useState(DEFAULT_END);
  const [activeRegionKey, setActiveRegionKey] = useState('all');

  const handleDatesChange = ({ start = DEFAULT_START, end = DEFAULT_END }) => {
    setStart(start);
    setEnd(end);
  };

  const { data: regionalStats, isFetching: loadingStats } =
    useGetComplianceAdminStatisticsQuery({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    });
  const { data: eventTypes } = useGetEventTypesForRDOQuery();
  const { data: awardTypes } = useGetAwardOptionsQuery();

  const downloadStatsAsCSV = useCallback(() => {
    const idMapping = Object.fromEntries(
      eventTypes
        .map((e) => [e.id, e.name])
        .concat(awardTypes.map((a) => [a.id, a.name]))
    );
    const csvData = regionalStats
      .map((s) => {
        const csv = statsJsonToCsv(
          s.data,
          start.format('YYYY-MM-DD'),
          end.format('YYYY-MM-DD'),
          true,
          idMapping
        );
        const meta = csv.slice(0, 3); // start date, end date, blank line
        const headers = csv.slice(3, 4).map((h) => 'region,' + h);
        const data = csv
          .slice(4)
          .map((d) => (s?.region_name ?? s.region) + ',' + d);
        return meta.concat(headers).concat(data).join('\n');
      })
      .join('\n\n');
    downloadCsv(`sb-event-stats-${dayjs().format('YYYY-MM-DD')}.csv`, csvData);
  }, [awardTypes, end, eventTypes, start, regionalStats]);

  return (
    <div className='admin-dashboard-metrics events-stats mg-bottom-30 h-fit'>
      <div className='admin-dashboard-metrics__header mg-top-30 mg-bottom-5'>
        <h1 className='gSemiBold mg-bottom-0'>Statistics</h1>
        <div className='admin-dashboard-metrics__date-filter'>
          <StatsDateRangeSelect
            initialValue={'week'}
            onDatesChange={handleDatesChange}
            showAllTime
          />
        </div>
        <Button
          className={
            'sb-blue-bg-500 hover:sb-blue-bg-700 gRegular br10 white txt-center h-fit border-0'
          }
          onClick={downloadStatsAsCSV}
          loading={loadingStats}
        >
          Export as CSV
        </Button>
      </div>

      <Tabs
        className={'mg-top-20'}
        activeKey={activeRegionKey}
        onChange={setActiveRegionKey}
        items={
          regionalStats
            ? [
                ...regionalStats.map((data, i) => ({
                  key: data.region ?? i,
                  label: (
                    <span className='cap gBold fs-20'>
                      {data?.region_name ?? data.region}
                    </span>
                  ),
                  children: (
                    <StatisticsTabs
                      statsData={data.data}
                      loading={loadingStats}
                      isSuperAdmin={true}
                    />
                  ),
                })),
              ]
            : null
        }
      />
    </div>
  );
}

export default ComplianceAdminStatistics;
